import type { SearchResult, TemplateType } from '@/services/auth/types';
import type { GroupType } from './result-metadata';

// Split template items into per-template_type entries, preserving first-occurrence order.
// Skips 'unset' — those are handled by remapUnsetTemplates before grouping.
function groupTemplateEntries(items: SearchResult[]): [GroupType, SearchResult[]][] {
  const order: TemplateType[] = [];
  const byType: Partial<Record<TemplateType, SearchResult[]>> = {};

  for (const item of items) {
    if (item.kind !== 'template') continue;
    const t = item.other.template_type;
    if (t === 'unset') continue;
    if (byType[t] == null) {
      order.push(t);
      byType[t] = [];
    }
    byType[t]!.push(item);
  }

  return order.map((t) => [t, byType[t]!] as [GroupType, SearchResult[]]);
}

// Split campaign items into regular campaigns and triggered_broadcast groups.
function groupCampaignEntries(items: SearchResult[]): [GroupType, SearchResult[]][] {
  const campaigns = items.filter(
    (item) => !(item.kind === 'campaign' && item.other.campaign_type === 'triggered_broadcast'),
  );
  const triggeredBroadcasts = items.filter(
    (item) => item.kind === 'campaign' && item.other.campaign_type === 'triggered_broadcast',
  );

  const result: [GroupType, SearchResult[]][] = [];
  if (campaigns.length > 0) result.push(['campaign', campaigns]);
  if (triggeredBroadcasts.length > 0) result.push(['triggered_broadcast', triggeredBroadcasts]);
  return result;
}

/**
 * The backend skips indexing templates with template_type "unset", so in practice
 * this remap should never fire for newly indexed content. It exists to handle any
 * "unset" templates that were already in the index before that skip was introduced.
 * If they do appear, remap them to kind "newsletter" so they group and render correctly.
 */
export function remapUnsetTemplates(results: SearchResult[]): SearchResult[] {
  return results.map((r) =>
    r.kind === 'template' && r.other.template_type === 'unset'
      ? ({
          ...r,
          kind: 'newsletter' as const,
          other: {
            ...r.other,
            id: r.other.newsletter_id ?? r.other.id,
            sent: 'false',
          },
        } satisfies SearchResult)
      : r,
  );
}

export function groupSearchResults(results: SearchResult[]) {
  // Bucket by kind first, preserving API order within each bucket.
  const resultsByKind = results.reduce(
    (groups, item) => {
      groups[item.kind] ??= [];
      groups[item.kind].push(item);
      return groups;
    },
    {} as Record<SearchResult['kind'], SearchResult[]>,
  );

  const entries: [GroupType, SearchResult[]][] = [];

  for (const [kind, items] of Object.entries(resultsByKind)) {
    if (kind === 'template') {
      entries.push(...groupTemplateEntries(items));
    } else if (kind === 'campaign') {
      entries.push(...groupCampaignEntries(items));
    } else {
      entries.push([kind as GroupType, items]);
    }
  }

  // Navigation results always appear first.
  entries.sort(([a], [b]) => (a === 'navigation' ? -1 : b === 'navigation' ? 1 : 0));

  return entries;
}
